import {
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Grid,
  Stack,
} from '@mui/material';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { DialogHeader } from '@/components/common/DialogHeader';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PrintIcon from '@mui/icons-material/Print';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import type { PurchaseOrder, PurchaseOrderStatus } from '@/types';
import { dateFnsLocale } from '@/utils/locale';

interface PurchaseOrderDetailModalProps {
  open: boolean;
  onClose: () => void;
  purchaseOrder: PurchaseOrder | null;
  onEdit?: (po: PurchaseOrder) => void;
  onApprove?: (po: PurchaseOrder) => void;
  onMarkOrdered?: (po: PurchaseOrder) => void;
  onMarkReceived?: (po: PurchaseOrder) => void;
  onPrint?: (po: PurchaseOrder) => void;
  onMatchInvoice?: (po: PurchaseOrder) => void;
}

const statusColors: Record<PurchaseOrderStatus, 'default' | 'warning' | 'info' | 'primary' | 'success' | 'error'> = {
  draft: 'default',
  approved: 'info',
  ordered: 'primary',
  partially_received: 'warning',
  received: 'success',
  cancelled: 'error',
};

export function PurchaseOrderDetailModal({
  open,
  onClose,
  purchaseOrder: po,
  onEdit,
  onApprove,
  onMarkOrdered,
  onMarkReceived,
  onPrint,
  onMatchInvoice,
}: PurchaseOrderDetailModalProps) {
  const { t } = useTranslation('inventory');
  const { t: tc } = useTranslation('common');

  const statusLabels: Record<PurchaseOrderStatus, string> = {
    draft: t('purchaseOrders.status.draft'),
    approved: t('purchaseOrders.status.approved'),
    ordered: t('purchaseOrders.status.ordered'),
    partially_received: t('purchaseOrders.status.partiallyReceived'),
    received: t('purchaseOrders.status.received'),
    cancelled: t('purchaseOrders.status.cancelled'),
  };

  if (!po) return null;

  const showReceivedColumn = po.status === 'received' || po.status === 'partially_received';

  return (
    <ResponsiveDialog mobileTitle={t('purchaseOrderDetail.purchaseOrder')} open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogHeader onClose={onClose}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6">{t('purchaseOrderDetail.purchaseOrder')} {po.orderNumber}</Typography>
            <Chip label={statusLabels[po.status]} color={statusColors[po.status]} size="small" />
          </Box>
          <Typography variant="body2" color="text.secondary">
            {po.supplier.name}
          </Typography>
        </Box>
      </DialogHeader>

      <DialogContent dividers>
        {/* Info Grid */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('purchaseOrderDetail.supplier')}
              </Typography>
              <Typography variant="body1" fontWeight={600} gutterBottom>
                {po.supplier.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('purchaseOrderDetail.deliveryTime', { days: po.supplier.deliveryDays })}
              </Typography>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('purchaseOrderDetail.dates')}
              </Typography>
              <Stack spacing={0.5}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">{t('purchaseOrderDetail.created')}:</Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {format(new Date(po.createdAt), 'd.M.yyyy HH:mm', { locale: dateFnsLocale() })}
                  </Typography>
                </Box>
                {po.approvedAt && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">{t('purchaseOrderDetail.approved')}:</Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {format(new Date(po.approvedAt), 'd.M.yyyy HH:mm', { locale: dateFnsLocale() })}
                    </Typography>
                  </Box>
                )}
                {po.orderedAt && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">{t('purchaseOrderDetail.ordered')}:</Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {format(new Date(po.orderedAt), 'd.M.yyyy HH:mm', { locale: dateFnsLocale() })}
                    </Typography>
                  </Box>
                )}
                {po.expectedDeliveryDate && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">{t('purchaseOrderDetail.expectedDelivery')}:</Typography>
                    <Typography variant="body2" fontWeight={500} color="primary">
                      {format(new Date(po.expectedDeliveryDate), 'd.M.yyyy', { locale: dateFnsLocale() })}
                    </Typography>
                  </Box>
                )}
                {po.receivedAt && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">{t('purchaseOrderDetail.received')}:</Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {format(new Date(po.receivedAt), 'd.M.yyyy HH:mm', { locale: dateFnsLocale() })}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('purchaseOrderDetail.creator')}
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {po.createdByName}
              </Typography>
            </Paper>
          </Grid>

          {po.approvedByName && (
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('purchaseOrderDetail.approver')}
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {po.approvedByName}
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Items Table */}
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          {t('purchaseOrderDetail.orderedParts')}
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('purchaseOrderForm.colPartNumber')}</TableCell>
                <TableCell>{t('purchaseOrderForm.colName')}</TableCell>
                <TableCell>{t('purchaseOrderDetail.destinationCol')}</TableCell>
                <TableCell align="right">{t('purchaseOrderForm.colQuantity')}</TableCell>
                <TableCell align="right">{t('purchaseOrderForm.colUnitPrice')}</TableCell>
                <TableCell align="right">{t('purchaseOrderForm.colTotal')}</TableCell>
                {showReceivedColumn && <TableCell align="right">{t('purchaseOrderDetail.received')}</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {po.items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace" fontWeight={500}>
                      {item.partNumber}
                    </Typography>
                  </TableCell>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>
                    {item.linkedBookingId ? (
                      <>
                        <Chip
                          size="small"
                          color="warning"
                          label={t('purchaseOrderDetail.destinationTransit')}
                        />
                        {item.linkedWorkOrder && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                            {t('purchaseOrderDetail.linkedWorkOrder')}:{' '}
                            {[item.linkedWorkOrder.vehicleRegistration, item.linkedWorkOrder.customerName]
                              .filter(Boolean)
                              .join(' · ')}
                          </Typography>
                        )}
                      </>
                    ) : (
                      <Chip size="small" variant="outlined" label={t('purchaseOrderDetail.destinationStock')} />
                    )}
                  </TableCell>
                  <TableCell align="right">{item.quantity} {t('purchaseOrderDetail.pcs')}</TableCell>
                  <TableCell align="right">€{(item.unitCostCents / 100).toFixed(2)}</TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={600}>
                      €{(item.totalCostCents / 100).toFixed(2)}
                    </Typography>
                  </TableCell>
                  {showReceivedColumn && (
                    <TableCell align="right">
                      <Chip
                        label={`${item.receivedQuantity ?? 0} ${t('purchaseOrderDetail.pcs')}`}
                        size="small"
                        color={item.receivedQuantity === item.quantity ? 'success' : 'warning'}
                      />
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider sx={{ my: 3 }} />

        {/* Cost Summary */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Box sx={{ minWidth: 300 }}>
            <Stack spacing={1}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">{t('purchaseOrderForm.subtotal')}</Typography>
                <Typography variant="body2">€{(po.subtotalCents / 100).toFixed(2)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">{t('purchaseOrderForm.vat', {
                  rate:
                    po.subtotalCents > 0
                      ? String(Math.round((po.taxCents / po.subtotalCents) * 1000) / 10).replace('.', ',')
                      : '25,5',
                })}</Typography>
                <Typography variant="body2">€{(po.taxCents / 100).toFixed(2)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">{t('purchaseOrderForm.shipping')}</Typography>
                <Typography variant="body2">€{(po.shippingCents / 100).toFixed(2)}</Typography>
              </Box>
              <Divider />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6">{t('purchaseOrderForm.total')}</Typography>
                <Typography variant="h6" color="primary">
                  €{(po.totalCents / 100).toFixed(2)}
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Box>

        {/* Invoice Matching */}
        {(po.invoiceMatched || po.status === 'received') && (
          <>
            <Divider sx={{ my: 3 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  {t('purchaseOrderDetail.invoiceMatch.title')}
                </Typography>
                {po.invoiceMatched ? (
                  <Stack spacing={0.5}>
                    <Box sx={{ display: 'flex', gap: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('purchaseOrderDetail.invoiceMatch.numberLabel')}: <strong>{po.supplierInvoiceNumber}</strong>
                      </Typography>
                      {po.supplierInvoiceDate && (
                        <Typography variant="body2" color="text.secondary">
                          {t('purchaseOrderDetail.invoiceMatch.dateLabel')}:{' '}
                          <strong>{format(new Date(po.supplierInvoiceDate), 'd.M.yyyy', { locale: dateFnsLocale() })}</strong>
                        </Typography>
                      )}
                      <Typography variant="body2" color="text.secondary">
                        {t('purchaseOrderDetail.invoiceMatch.totalLabel')}:{' '}
                        <strong>€{((po.supplierInvoiceTotalCents ?? 0) / 100).toFixed(2)}</strong>
                      </Typography>
                    </Box>
                    <Box>
                      {po.invoiceDiscrepancyCents === 0 ? (
                        <Chip
                          size="small"
                          color="success"
                          label={t('purchaseOrderDetail.invoiceMatch.exactMatch')}
                        />
                      ) : (
                        <Chip
                          size="small"
                          color="warning"
                          label={t('purchaseOrderDetail.invoiceMatch.discrepancy', {
                            amount: ((po.invoiceDiscrepancyCents ?? 0) / 100).toFixed(2),
                          })}
                        />
                      )}
                    </Box>
                    {po.invoiceMatchedByName && po.invoiceMatchedAt && (
                      <Typography variant="caption" color="text.secondary">
                        {t('purchaseOrderDetail.invoiceMatch.matchedBy', {
                          name: po.invoiceMatchedByName,
                          date: format(new Date(po.invoiceMatchedAt), 'd.M.yyyy HH:mm', { locale: dateFnsLocale() }),
                        })}
                      </Typography>
                    )}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t('purchaseOrderDetail.invoiceMatch.notMatched')}
                  </Typography>
                )}
              </Box>
              {!po.invoiceMatched && onMatchInvoice && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ReceiptLongIcon />}
                  onClick={() => { onMatchInvoice(po); }}
                >
                  {t('purchaseOrderDetail.invoiceMatch.matchButton')}
                </Button>
              )}
            </Box>
          </>
        )}

        {/* Notes */}
        {po.notes && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography variant="subtitle2" gutterBottom>
              {t('purchaseOrderDetail.notes')}
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
              <Typography variant="body2">{po.notes}</Typography>
            </Paper>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
          <Box>
            {onPrint && (
              <Button startIcon={<PrintIcon />} onClick={() => onPrint(po)}>
                {t('purchaseOrderDetail.print')}
              </Button>
            )}
          </Box>
          <Stack direction="row" spacing={1}>
            <Button onClick={onClose}>{tc('close')}</Button>
            {po.status === 'draft' && onEdit && (
              <Button variant="outlined" startIcon={<EditIcon />} onClick={() => onEdit(po)}>
                {tc('edit')}
              </Button>
            )}
            {po.status === 'draft' && onApprove && (
              <Button variant="contained" startIcon={<CheckCircleIcon />} onClick={() => onApprove(po)}>
                {t('purchaseOrders.approve')}
              </Button>
            )}
            {po.status === 'approved' && onMarkOrdered && (
              <Button variant="contained" startIcon={<LocalShippingIcon />} onClick={() => onMarkOrdered(po)}>
                {t('purchaseOrders.markOrdered')}
              </Button>
            )}
            {(po.status === 'ordered' || po.status === 'partially_received') && onMarkReceived && (
              <Button variant="contained" startIcon={<CheckCircleIcon />} onClick={() => onMarkReceived(po)}>
                {t('purchaseOrders.markReceived')}
              </Button>
            )}
          </Stack>
        </Box>
      </DialogActions>
    </ResponsiveDialog>
  );
}
